import { format, toZonedTime } from 'date-fns-tz';
import { Image } from 'expo-image';
import type { ImageStyle, StyleProp, ViewStyle } from 'react-native';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api/reservations/types';
import {
  CDW_AGREE_TEXT,
  CDW_DECLINE_TEXT,
  LIABILITY_ACKNOWLEDGMENT_TEXT,
  SIGNATURE_AGREEMENT_PARAGRAPHS,
} from '@/components/rental-terms/copy';
import { GREY_30 } from '@/style/colors';
import { Card, Text } from '@/ui';

type SignedSignaturesSectionProps = {
  resData: Reservation | undefined;
  cardStyle?: StyleProp<ViewStyle>;
};

function formatSignatureDate(
  resData: Reservation | undefined
): string | undefined {
  if (!resData?.contractSignedAt) return undefined;

  return format(
    toZonedTime(resData.contractSignedAt, resData.pickupLocation.timezone),
    'yyyy MMM dd - hh:mm a'
  );
}

function SignatureImage({
  source,
  height = 150,
  imageStyle,
}: {
  source: string | undefined;
  height?: number;
  imageStyle?: ImageStyle;
}) {
  if (!source) {
    return (
      <View
        style={{
          width: '100%',
          height,
          backgroundColor: GREY_30,
          borderRadius: 16,
        }}
      />
    );
  }

  return (
    <Image
      source={source}
      style={{ width: '100%', height, borderRadius: 16, ...imageStyle }}
      contentFit="fill"
    />
  );
}

function SignatureDateRow({
  date,
  secondaryColor,
}: {
  date: string | undefined;
  secondaryColor: string;
}) {
  return (
    <View style={{ flexDirection: 'row', gap: 8 }}>
      <Text type="caption1">Signature Date</Text>
      <Text type="caption1" style={{ color: secondaryColor }}>
        {date}
      </Text>
    </View>
  );
}

function CardHeader({
  title,
  subtitle,
  borderColor,
  subtitleColor,
}: {
  title: string;
  subtitle?: string;
  borderColor: string;
  subtitleColor?: string;
}) {
  return (
    <View
      style={{
        borderBottomColor: borderColor,
        borderBottomWidth: 1,
      }}
    >
      <View style={{ padding: 16, gap: subtitle ? 4 : 0 }}>
        <Text type="title2">{title}</Text>
        {subtitle && (
          <Text type="bodyRegular" style={{ color: subtitleColor }}>
            {subtitle}
          </Text>
        )}
      </View>
    </View>
  );
}

export function SignedSignaturesSection({
  resData,
  cardStyle,
}: SignedSignaturesSectionProps) {
  const { theme } = useStyles(stylesheet);
  const signatureDate = formatSignatureDate(resData);
  const secondaryColor = theme.colors.text.secondary;
  const secondaryText = { color: secondaryColor };
  const borderColor = theme.colors.card.default.borderColor;

  return (
    <>
      <Card style={cardStyle}>
        <CardHeader title="Signature" borderColor={borderColor} />

        <View style={{ padding: 16, gap: 16 }}>
          <SignatureImage
            source={resData?.contractSignatureData}
            height={250}
          />

          {SIGNATURE_AGREEMENT_PARAGRAPHS.map((paragraph, index) => (
            <Text key={`signature-${index}`} type="bodyRegular">
              {paragraph}
            </Text>
          ))}

          <SignatureDateRow
            date={signatureDate}
            secondaryColor={secondaryColor}
          />
        </View>
      </Card>

      <Card style={cardStyle}>
        <CardHeader
          title="Collision Damage Waiver (CDW)"
          subtitle="Standard & Premium Protection"
          borderColor={borderColor}
          subtitleColor={secondaryColor}
        />

        <View style={{ justifyContent: 'space-between', flexGrow: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={{ padding: 16, gap: 16, width: '50%' }}>
              <SignatureImage
                source={
                  resData?.cdwEnabled
                    ? resData.contractSignatureData
                    : undefined
                }
              />
              <Text type="bodyRegular" style={secondaryText}>
                {CDW_AGREE_TEXT}
              </Text>
            </View>

            <View style={{ padding: 16, gap: 16, width: '50%' }}>
              <SignatureImage
                source={
                  resData?.cdwEnabled
                    ? undefined
                    : resData?.contractSignatureData
                }
                imageStyle={
                  resData?.contractSignatureData
                    ? { backgroundColor: GREY_30 }
                    : undefined
                }
              />
              <Text type="bodyRegular" style={secondaryText}>
                {CDW_DECLINE_TEXT}
              </Text>
            </View>
          </View>

          <View style={{ padding: 16 }}>
            <SignatureDateRow
              date={signatureDate}
              secondaryColor={secondaryColor}
            />
          </View>
        </View>
      </Card>

      <Card style={cardStyle}>
        <CardHeader
          title="Liability Coverage Acknowledgment"
          borderColor={borderColor}
        />

        <View style={{ justifyContent: 'space-between', flexGrow: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={{ padding: 16, gap: 16, width: '100%' }}>
              <SignatureImage
                source={
                  resData?.rcliEnabled
                    ? resData.contractSignatureData
                    : undefined
                }
                imageStyle={
                  resData?.rcliEnabled
                    ? { backgroundColor: GREY_30, borderWidth: 1 }
                    : undefined
                }
              />
              <Text type="bodyRegular" style={secondaryText}>
                {LIABILITY_ACKNOWLEDGMENT_TEXT}
              </Text>
            </View>
          </View>

          <View style={{ padding: 16 }}>
            <SignatureDateRow
              date={signatureDate}
              secondaryColor={secondaryColor}
            />
          </View>
        </View>
      </Card>
    </>
  );
}

const stylesheet = createStyleSheet(() => ({}));
